import { ReactElement, useState, useContext, useEffect, useMemo } from 'react';
import moment from 'moment';
import { BasketContext, BasketItem } from '@context/BasketContext';
import { AuthContext } from '@context/AuthContext';
import { ModalsContext } from '@root/context/ModalsContext';
import formStyles from '../../Form/Form.module.scss';
import Persons from '../../Form/Persons';
import Button from '../../Button/Button';
import Daypicker from '../../Form/Daypicker';
import style from './Availability.module.scss';
import Stl from '@root/lib/Stl';
import { fetchAPI } from '@root/lib/fetchAPI';
import { AVAILABILITY_BY_DATE_BY_LISTING_ID } from '@queries/listings';

import { workspaces as workspaceLabels } from '@root/data/workspaces';

const st = (new Stl()).bind(style);

interface Props {
  workspaces: Dict[];
  selectedDays: Date[];
  listing: Dict;
  personsAmount: number;
}

const workspacesPersonsAmount = {
  FlexDesk: 1,
  Workstation: 1,
  PrivateOffice: 1,
  MeetingRoomSmall: 4,
  MeetingRoomMedium: 8,
  MeetingRoomLarge: 20,
};

const getMinDate = () => {
  if (moment().hour() > 12) {
    return [ moment().add(2, 'days').toDate() ];
  }
  return [ moment().add(1, 'days').toDate() ];
};

export default function LocationDetailAvailability(props: Props): ReactElement {
  const auth = useContext(AuthContext);
  const modalsContext = useContext(ModalsContext);
  const basket = useContext(BasketContext);
  const [ personsAmount, setPersonsAmount ] = useState<number>(props.personsAmount || 1);
  const [ listingAvailability, setListingAvailability ] = useState<Dict>();
  const [ selectedDays, setSelectedDays ] = useState(props.selectedDays || getMinDate());

  useEffect(() => {
    checkAvailability();
  }, [ selectedDays, personsAmount ]);

  const handleDayClick = (day) => {
    setSelectedDays([ day ]);
  };

  const startCheckout = (workspace: Dict) => {
    if (!auth.signedIn) {
      modalsContext.setInternalAuthModalStep('login');
      modalsContext.setModalVisibleSignin(true);

      return;
    }

    console.log('> Checkout', {
      personsAmount,
      listing: props.listing,
      date: selectedDays[0],
      workspace: workspace,
      quantity: workspace.convertedPersonsAmount,
    });

    basket.setBasketItems([
      new BasketItem({
        personsAmount,
        listing: props.listing,
        date: moment(selectedDays[0]).format().split('T')[0],
        workspace: workspace,
        quantity: workspace.convertedPersonsAmount,
      }),
    ]);

    basket.goToCheckout();
  };

  const renderAmenities = (workspace) => {
    const maxItems = 5;

    const items = workspace.listings_workspaces_amenities
      .map(({ workspace_amenity }, workspace_amenityIndex) => (
        <span key={ workspace_amenity.id }>
          { workspace_amenityIndex > 0 && ', '}
          <span>{ workspace_amenity.name }</span>
        </span>
      ))
      .filter((workspace_amenity, workspace_amenityIndex) => workspace_amenityIndex < maxItems);

    if (workspace.listings_workspaces_amenities.length > maxItems) {
      items.push(
        <span key={ 'more-items' }>
          { ', '}
          <span>+{ workspace.listings_workspaces_amenities.length - maxItems } more workspace amenities</span>
        </span>
      );
    }

    return items;
  };

  const checkAvailability = async () => {
    const selectedDaysCleaned = selectedDays.map(day => moment(day).format().split('T')[0]);

    console.log('>>>', selectedDays, props.selectedDays, selectedDaysCleaned);

    const queryData = await fetchAPI(AVAILABILITY_BY_DATE_BY_LISTING_ID, {
      variables: {
        listing_id: props.listing.id,
        date: selectedDaysCleaned,
      },
    });

    const availabilityResult = queryData.data.listings_availability[0];

    setListingAvailability(availabilityResult);
  };

  const availableWorkspaces = useMemo(() => {
    if (!listingAvailability?.listing) {
      return;
    }

    const bookingsPerType = {};

    listingAvailability.listing.bookings.forEach(booking => {
      if (!bookingsPerType[booking.workspace_type]) {
        bookingsPerType[booking.workspace_type] = 0;
      }

      bookingsPerType[booking.workspace_type] += booking.persons_amount;
    });

    // Check if selected day is too soon (tomorrow after 12:00/less than 12 hours in advance)
    const now = new Date();
    // Reset the date to midnight
    selectedDays[0].setHours(0, 0, 0, 0);
    const selectedDate = selectedDays[0];
    const timeDifference = (selectedDate.getTime() - now.getTime()) / 1000;
    const tooLate = timeDifference < 43200; // 12 hours in seconds

    return JSON.parse(JSON.stringify(props.workspaces)).map(workspace => {
      // Cross-check if the workspace has already been fully booked or
      // the available spaces is less than selected personsAmount
      workspace.available = true;

      // Remove the bookings of this workspace type from the available amount
      workspace.amount -= bookingsPerType[workspace.type] || 0;
      workspace.convertedPersonsAmount = Math.ceil(personsAmount / workspacesPersonsAmount[workspace.type]);

      if (tooLate) {
        workspace.available = false;
      }

      if (workspace.convertedPersonsAmount > workspace.amount) {
        workspace.available = false;
      }

      return workspace;
    });
  }, [ props.workspaces, listingAvailability, personsAmount ]);

  return (
    <div className={ st`availability` }>
      <div className={ st`form` }>
        <Daypicker
          onDayClick={ handleDayClick }
          selectedDays={ selectedDays }
          variant='availability'
          label="Booking date"
        />
        <label className={ formStyles.label }>
          <span>Number of people</span>
          <Persons
            className={ st`persons persons--no-button` }
            personsAmount={ personsAmount }
            setPersonsAmount={ setPersonsAmount }
          />
        </label>
      </div>
      <div className={ st`results` }>
        <ul className={ st`resultsList` }>
          { listingAvailability?.listing_id && availableWorkspaces
            .filter(workspace => workspace.amount !== 0 && workspace.price > 0)
            .map((workspace, workspaceIndex) => (
              <li className={ st`resultsItem` } key={ workspaceIndex }>
                <div className={ st`resultsIcon` }>
                  <div
                    className={ st`icon` }
                    style={ { maskImage: `url("/images/icons/workspaces/${ workspace.type }.svg")`, WebkitMaskImage: `url("/images/icons/workspaces/${ workspace.type }.svg")` } }
                  />
                </div>
                <div className={ st`resultsDescription` }>
                  <h5 className={ st`resultsType` }><span>{ workspace.convertedPersonsAmount }x</span>{ workspaceLabels[workspace.type] }</h5>
                  <div className={ st`resultsAmenities` }>
                    { workspacesPersonsAmount[workspace.type] } Person{ workspacesPersonsAmount[workspace.type] > 1 ? 's ' : '' } • { renderAmenities(workspace) }
                  </div>
                </div>
                <div className={ st`actions` }>
                  { workspace.type.indexOf('MeetingRoom') > -1
                    ? <div className={ st`resultsPrice` }>
                      <h5>€{ workspace.price * workspace.convertedPersonsAmount } total</h5>
                      <small>€{ workspace.price } / room per day (incl. BTW)</small>
                    </div>
                    : <div className={ st`resultsPrice` }>
                      <h5>€{ workspace.price * workspace.convertedPersonsAmount } total</h5>
                      <small>€{ workspace.price } / person per day (incl. BTW)</small>
                    </div>
                  }

                  <Button
                    className={ st`button-book` }
                    onClick={ () => startCheckout(workspace) }
                    disabled={ !workspace.available }
                  >
                    { workspace.available
                      ? <>Book now</>
                      : <>Not available</>
                    }
                  </Button>
                </div>
              </li>
            ))
          }
          { !listingAvailability?.listing_id &&
            <>
              <h4>This facility is closed on this day. Please select another day above.</h4>
            </>
          }
        </ul>
      </div>
    </div>
  );
}
